import React, { useState, useEffect, useRef } from 'react';
import { Link, Outlet, useLocation, Navigate } from 'react-router-dom';
import { Navbar, Footer } from 'flowbite-react';
import { Sun, Moon, Menu, X } from 'lucide-react';
import { useDarkMode } from '../contexts/DarkModeContext';
import { useAuth } from '../contexts/AuthContext';
import AppLanding from './app/AppLanding';
import AppLogin from './app/AppLogin';
import AppSignUp from './app/AppSignUp';
import AppTermsOfService from './app/AppTermsOfService';
import AppPrivacyPolicy from './app/AppPrivacyPolicy';
import AppForgotPassword from './app/AppForgotPassword';
import ForgotPassword from './ForgotPassword';

const PublicLayout = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const location = useLocation();
  const { currentUser } = useAuth();
  const mainRef = useRef(null);

  useEffect(() => {
    const mainElement = mainRef.current;

    const handleScroll = () => {
      if (!mainElement) return;

      const isScrolled = mainElement.scrollTop > 20;
      
      setScrolled(prevScrolled => {
        if (isScrolled !== prevScrolled) {
          return isScrolled;
        }
        return prevScrolled;
      });
    };

    if (mainElement) {
      mainElement.addEventListener('scroll', handleScroll);
      
      handleScroll();
      
      return () => {
        mainElement.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  const isNative = typeof window !== 'undefined' && window.isUmmahtiNative;
  const currentPath = location.pathname;

  const MobileMenu = () => (
    <>
      <div 
        className={`fixed inset-0 bg-black/30 dark:bg-black/60 backdrop-blur-sm z-50 transition-opacity duration-300 ${
          isMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsMenuOpen(false)}
      />
      
      <div 
        className={`fixed right-0 top-0 bottom-0 w-[320px] z-50 transform transition-transform duration-300 ease-out ${
          isMenuOpen ? 'translate-x-0' : 'translate-x-full'
        } ${isDarkMode ? 'bg-gradient-to-b from-[#121212] to-[#0A0A0A]' : 'bg-gradient-to-b from-white to-gray-50'} shadow-xl`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-6 border-b border-gray-800/10 dark:border-white/10" 
            style={{ paddingTop: 'calc(env(safe-area-inset-top) + 1.5rem)' }}
          >
            <h2 className={`text-lg font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Menu
            </h2>
            <button
              onClick={() => setIsMenuOpen(false)}
              className={`p-2 rounded-full transition-colors ${
                isDarkMode 
                  ? 'bg-white/5 hover:bg-white/10' 
                  : 'bg-gray-100 hover:bg-gray-200'
              }`}
            >
              <X className={isDarkMode ? 'text-white' : 'text-gray-900'} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            <div className="p-6 space-y-6">
              <div className={`p-5 rounded-xl ${
                isDarkMode ? 'bg-white/5 shadow-inner shadow-black/20' : 'bg-gray-50 shadow-inner'
              }`}>
                <div className="flex items-center justify-between">
                  <span className={`font-medium ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {isDarkMode ? 'Dark Mode' : 'Light Mode'}
                  </span>
                  <button
                    onClick={toggleDarkMode}
                    className={`p-2.5 rounded-xl transition-all duration-300 ${
                      isDarkMode 
                        ? 'bg-emerald-900/30 hover:bg-emerald-900/50 text-emerald-500' 
                        : 'bg-white hover:bg-gray-100 shadow-sm text-emerald-700'
                    }`}
                  >
                    {isDarkMode ? (
                      <Sun className="w-5 h-5" />
                    ) : (
                      <Moon className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                <Link 
                  to="/login"
                  onClick={() => setIsMenuOpen(false)}
                  className={`flex items-center w-full px-5 py-4 rounded-xl transition-colors ${
                    currentPath === '/login'
                      ? isDarkMode 
                        ? 'bg-emerald-900/30 text-emerald-400 font-medium' 
                        : 'bg-emerald-50 text-emerald-700 font-medium'
                      : isDarkMode 
                        ? 'hover:bg-white/5 text-white' 
                        : 'hover:bg-gray-100 text-gray-900'
                  }`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                  </svg>
                  Login
                </Link>
                <Link 
                  to="/signup"
                  onClick={() => setIsMenuOpen(false)}
                  className={`flex items-center w-full px-5 py-4 rounded-xl transition-colors ${
                    currentPath === '/signup'
                      ? isDarkMode 
                        ? 'bg-emerald-900/30 text-emerald-400 font-medium' 
                        : 'bg-emerald-50 text-emerald-700 font-medium'
                      : isDarkMode 
                        ? 'hover:bg-white/5 text-white' 
                        : 'hover:bg-gray-100 text-gray-900'
                  }`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                  Sign Up
                </Link>
              </div>
              
              <div className="pt-4 border-t border-gray-200 dark:border-gray-800">
                <h3 className={`text-xs font-semibold uppercase tracking-wider mb-4 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Explore
                </h3>
                <div className="space-y-3">
                  <Link 
                    to="/about"
                    onClick={() => setIsMenuOpen(false)}
                    className={`flex items-center w-full px-5 py-3 rounded-xl transition-colors ${
                      isDarkMode 
                        ? 'hover:bg-white/5 text-gray-300' 
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    About Us
                  </Link>
                  <Link 
                    to="/contact"
                    onClick={() => setIsMenuOpen(false)}
                    className={`flex items-center w-full px-5 py-3 rounded-xl transition-colors ${
                      isDarkMode 
                        ? 'hover:bg-white/5 text-gray-300' 
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    Contact
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  if (isNative) {
    if (currentUser) {
      if (currentPath !== '/dashboard') {
        return <Navigate to="/dashboard" replace />;
      }
    } else {
      if (currentPath === '/') {
        return <AppLanding />;
      } else if (currentPath === '/login') {
        return <AppLogin />;
      } else if (currentPath === '/signup') {
        return <AppSignUp />;
      } else if (currentPath === '/app/terms') {
        return <AppTermsOfService />;
      } else if (currentPath === '/app/privacy') {
        return <AppPrivacyPolicy />;
      } else if (currentPath === '/app/forgot-password') {
        return <AppForgotPassword />;
      } else if (currentPath === '/forgot-password') {
        return <ForgotPassword />;
      }
    }
  }

  const isCleanLayoutRoute = currentPath === '/forgot-password' || 
                            currentPath === '/app/forgot-password' || 
                            currentPath === '/finalize-password-reset' || 
                            currentPath.startsWith('/__/auth/action');
  
  if (isCleanLayoutRoute) {
    return <Outlet />;
  }

  return (
    <div className={`flex flex-col min-h-screen transition-colors duration-200 ${
      isDarkMode ? 'bg-[#0A0A0A]' : 'bg-gray-50'
    }`}>
      <Navbar 
        fluid 
        className={`fixed w-full z-40 transition-all duration-300 ${
          scrolled 
            ? 'sm:bg-transparent bg-opacity-95 border-0 ' + (isDarkMode ? 'bg-[#0A0A0A]' : 'bg-white')
            : 'bg-transparent border-transparent'
        }`}
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 w-full">
          <div className="w-full flex justify-between items-center py-3">
            <Navbar.Brand
              as={Link}
              to="/"
              className={`flex items-center gap-2 transition-all duration-300 ${
                !(location.pathname === '/' && !scrolled) && 'sm:' // Only apply pill on desktop
                  ? isDarkMode
                    ? 'sm:bg-[#111111] sm:hover:bg-[#1A1A1A] sm:shadow-sm sm:rounded-full sm:px-4 sm:py-2.5'
                    : 'sm:bg-white sm:hover:bg-gray-50 sm:shadow-sm sm:rounded-full sm:px-4 sm:py-2.5'
                  : ''
              }`}
            >
              <img
                src={
                  (location.pathname === '/' && !scrolled) || isDarkMode
                    ? "/images/logo_white.svg"
                    : "/images/logo_green.svg"
                }
                className="h-12 transition-opacity duration-300"
                alt="Ummahti Logo"
              />
            </Navbar.Brand>

            <div className="hidden sm:flex items-center gap-4">
              <div className={`flex items-center gap-3 px-4 py-2 rounded-full transition-all duration-300 relative h-12 ${
                isDarkMode 
                  ? 'bg-[#111111] hover:bg-[#1A1A1A] shadow-sm' 
                  : 'bg-white hover:bg-gray-50 shadow-sm'
              }`}>
                <div 
                  className={`absolute inset-2 transition-all duration-500 ease-in-out rounded-full ${
                    isDarkMode 
                      ? 'bg-emerald-500 shadow-lg shadow-emerald-500/20' 
                      : 'bg-gray-900'
                  }`}
                  style={{
                    left: location.pathname === '/login' ? '8px' : '50%',
                    right: location.pathname === '/signup' ? '8px' : '50%',
                    opacity: (location.pathname === '/login' || location.pathname === '/signup') ? 1 : 0,
                  }}
                />
                <Link 
                  to="/login" 
                  className={`px-5 py-2 rounded-full transition-all duration-300 relative z-10 flex items-center ${
                    location.pathname === '/login' 
                      ? isDarkMode 
                        ? 'text-black font-medium' 
                        : 'text-white font-medium'
                      : isDarkMode 
                        ? 'text-gray-400 hover:text-white' 
                        : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Login
                </Link>
                <Link 
                  to="/signup" 
                  className={`px-5 py-2 rounded-full transition-all duration-300 relative z-10 flex items-center ${
                    location.pathname === '/signup' 
                      ? isDarkMode 
                        ? 'text-black font-medium' 
                        : 'text-white font-medium'
                      : isDarkMode 
                        ? 'text-gray-400 hover:text-white' 
                        : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Sign Up
                </Link>
              </div>

              <button
                onClick={toggleDarkMode}
                className={`h-12 px-4 rounded-full transition-all duration-300 flex items-center justify-center ${
                  isDarkMode 
                    ? 'bg-[#111111] hover:bg-[#1A1A1A] shadow-sm' 
                    : 'bg-white hover:bg-gray-50 shadow-sm'
                }`}
              >
                {isDarkMode ? (
                  <Sun className="w-5 h-5 text-emerald-400 hover:text-emerald-300 transition-colors" />
                ) : (
                  <Moon className="w-5 h-5 text-gray-600 hover:text-gray-900 transition-colors" />
                )}
              </button>
            </div>

            <button
              onClick={() => setIsMenuOpen(true)}
              className={`sm:hidden p-3 rounded-full transition-colors h-12 flex items-center justify-center ${
                isDarkMode 
                  ? (scrolled ? 'bg-[#111111]' : 'bg-[#111111]/70') + ' hover:bg-[#1A1A1A]' 
                  : (scrolled ? 'bg-white' : 'bg-white/80') + ' hover:bg-gray-50'
              } backdrop-blur-sm shadow-sm`}
            >
              <Menu className={`w-6 h-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`} />
            </button>
          </div>
        </div>
      </Navbar>

      <MobileMenu />

      <div ref={mainRef} className="flex-1 overflow-y-auto">
        <div>
          <Outlet />
        </div>

        {/* Solid background transition to footer */}
        <div className={`relative z-10 h-12 ${
          isDarkMode 
            ? 'bg-gradient-to-b from-transparent to-[#111111]' 
            : 'bg-gradient-to-b from-transparent to-white'
        }`}></div>

        <Footer container={false} className={`transition-all duration-200 !rounded-none border-t relative z-20 ${
          isDarkMode 
            ? 'bg-[#111111] border-gray-800' 
            : 'bg-white border-gray-200'
        }`} style={{ backgroundColor: isDarkMode ? '#111111' : 'white', opacity: 1 }}>
          <div className="w-full">
            <div className="container mx-auto px-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 py-12">
                <div className="space-y-6">
                  <img
                    src={isDarkMode ? "/images/logo_white.png" : "/images/logo_green.png"}
                    className="h-8"
                    alt="Ummahti Logo"
                  />
                  <div className="space-y-4">
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      A premium Islamic experience for the modern Muslim
                    </p>
                    <p className={`pt-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      © {new Date().getFullYear()} Ummahti. All rights reserved.
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className={`text-sm font-semibold mb-4 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>Quick Links</h3>
                  <ul className="space-y-3">
                    <li>
                      <Link 
                        to="/about" 
                        className={`text-sm transition-colors ${
                          isDarkMode 
                            ? 'text-gray-400 hover:text-white' 
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        About Us
                      </Link>
                    </li>
                    <li>
                      <Link 
                        to="/privacy" 
                        className={`text-sm transition-colors ${
                          isDarkMode 
                            ? 'text-gray-400 hover:text-white' 
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        Privacy Policy
                      </Link>
                    </li>
                    <li>
                      <Link 
                        to="/contact" 
                        className={`text-sm transition-colors ${
                          isDarkMode 
                            ? 'text-gray-400 hover:text-white' 
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        Contact
                      </Link>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className={`text-sm font-semibold mb-4 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>Features</h3>
                  <ul className="space-y-3">
                    <li>
                      <Link 
                        to="/login" 
                        className={`text-sm transition-colors ${
                          isDarkMode 
                            ? 'text-gray-400 hover:text-white' 
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        Prayer Times
                      </Link>
                    </li>
                    <li>
                      <Link 
                        to="/login" 
                        className={`text-sm transition-colors ${
                          isDarkMode 
                            ? 'text-gray-400 hover:text-white' 
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        Quran
                      </Link>
                    </li>
                    <li>
                      <Link 
                        to="/login" 
                        className={`text-sm transition-colors ${
                          isDarkMode 
                            ? 'text-gray-400 hover:text-white' 
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        Deen Helpers
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </Footer>
      </div>
    </div>
  );
};

export default PublicLayout; 